import React, { useMemo } from 'react';

import { Box, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import type { AssetProcessingActivitiesMetrics } from 'config/api/processingActivities/processingActivities.types';
import processing_activities_messages from 'messages/processing_activities_messages';

import useStyles from '../ProcessingActivityDetailPage.styles';

type Props = {
  isLoading: boolean;
  metrics?: AssetProcessingActivitiesMetrics;
};

type MetricTileConfig = {
  badgeClassName: string;
  key: keyof AssetProcessingActivitiesMetrics;
  title: string;
};

const ProcessingActivityDetailMetricsSection: React.FC<Props> = ({ isLoading, metrics }) => {
  const { t } = useTranslation();
  const styles = useStyles();

  const tiles: MetricTileConfig[] = useMemo(
    () => [
      {
        key: 'linkedAssetProcessingsCount',
        title: t(processing_activities_messages.detail.metrics.linked_asset_processings),
        badgeClassName: styles.metricBadgeInfo,
      },
      {
        key: 'awaitingResponseCount',
        title: t(processing_activities_messages.detail.metrics.awaiting_response),
        badgeClassName: styles.metricBadgeWarn,
      },
      {
        key: 'submittedCount',
        title: t(processing_activities_messages.detail.metrics.submitted),
        badgeClassName: styles.metricBadgeInfo,
      },
      {
        key: 'acceptedCount',
        title: t(processing_activities_messages.detail.metrics.accepted),
        badgeClassName: styles.metricBadgeSuccess,
      },
      {
        key: 'dpiaIndicatedCount',
        title: t(processing_activities_messages.detail.metrics.dpia_indicated),
        badgeClassName: styles.metricBadgeDanger,
      },
      {
        key: 'sensitivePersonalDataCount',
        title: t(processing_activities_messages.detail.metrics.sensitive_personal_data),
        badgeClassName: styles.metricBadgeWarn,
      },
    ],
    [styles, t],
  );

  const getTileValue = (key: keyof AssetProcessingActivitiesMetrics): string => {
    if (isLoading && !metrics) {
      return '—';
    }

    return String(metrics?.[key] ?? 0);
  };

  return (
    <Box aria-label={t(processing_activities_messages.detail.metrics.aria_label)} className={styles.metricsRow} role='region'>
      {tiles.map(tile => {
        const value = getTileValue(tile.key);

        return (
          <Box key={tile.key} aria-label={`${tile.title}: ${value}`} className={styles.metricTile} role='group'>
            <Box className={styles.metricCard}>
              <Typography className={styles.metricLabel} color='textSecondary' variant='body2'>
                {tile.title}
              </Typography>
              <div className={styles.metricValueLine}>
                <Typography className={styles.metricValue} component='div' variant='h4'>
                  {value}
                </Typography>
                <span className={clsx(styles.metricBadge, tile.badgeClassName)}>{value}</span>
              </div>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default ProcessingActivityDetailMetricsSection;
